import { useQueryClient } from "@tanstack/react-query";
import type React from "react";
import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AuthProvider as OidcAuthProvider, useAuth } from "react-oidc-context";
import { Button } from "@/components/ui/button";
import { getOidcConfig } from "@/config/auth";
import { PAGE_PATHS } from "@/config/urls";
import { useAuthFlow } from "@/hooks/useAuthFlow";
import { userQueryKeys, useUser } from "@/hooks/useUser";
import { setAuthFlowHandler } from "@/lib/axios";
import { authApi } from "@/lib/humhub-api/auth";
import type { User } from "@/lib/humhub-api/types";
import * as TokenUtils from "@/lib/TokenManager";

interface AuthContextType {
  isAuthReady: boolean;
  retry: () => void;
}

const AuthContext = createContext<AuthContextType>({
  isAuthReady: false,
  retry: () => {},
});

export const useAuthContext = () => useContext(AuthContext);

// TanStack Query ベースのユーザー情報取得フック
export const useGlobalUserInfo = (): User | null => {
  const { currentUser } = useUser();
  return currentUser || null;
};

// ユーザー情報を更新する統一関数
export const updateGlobalUserInfo = (
  queryClient: ReturnType<typeof useQueryClient>,
  userInfo: User | null,
) => {
  if (userInfo) {
    // TanStack Query のキャッシュを更新
    queryClient.setQueryData(userQueryKeys.current(), userInfo);
    if (userInfo.id) {
      queryClient.setQueryData(userQueryKeys.byId(userInfo.id), userInfo);
    }
  } else {
    // ユーザー情報をクリア
    queryClient.setQueryData(userQueryKeys.current(), null);
    queryClient.invalidateQueries({ queryKey: userQueryKeys.all });
  }
};

interface AuthProviderProps {
  children: React.ReactNode;
}

/**
 * OIDC関連のブラウザストレージデータをクリーンアップ
 */
const cleanupOidcStorage = () => {
  try {
    const sessionKeys = Object.keys(sessionStorage);
    sessionKeys.forEach((key) => {
      if (key.includes("oidc.")) {
        sessionStorage.removeItem(key);
      }
    });

    const localKeys = Object.keys(localStorage);
    localKeys.forEach((key) => {
      if (key.includes("oidc.")) {
        localStorage.removeItem(key);
      }
    });
  } catch (error) {
    console.error("❌ Error during OIDC storage cleanup:", error);
  }
};

/**
 * 認証状態管理コンポーネント
 */
const AuthStateManager: React.FC<{
  children: React.ReactNode;
  setIsAuthReady: (ready: boolean) => void;
}> = ({ children, setIsAuthReady }) => {
  const auth = useAuth();
  const authFlow = useAuthFlow();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  /**
   * 元のURLにリダイレクト
   */
  const redirectToOriginalUrl = useCallback(() => {
    try {
      const originalUrl = TokenUtils.getOriginalUrl();
      if (originalUrl) {
        TokenUtils.clearOriginalUrl();
        const url = new URL(originalUrl);
        if (url.origin === window.location.origin) {
          const path = url.pathname + url.search;
          window.location.href = path || "/";
        } else {
          window.location.href = originalUrl;
        }
      } else {
        window.location.href = "/";
      }
    } catch (_error) {
      window.location.href = "/";
    }
  }, []);

  // 状態管理（ref使用で再レンダリングを防ぐ）
  const isInitialized = useRef(false);
  const isProcessing = useRef(false);
  const hasCheckedAuth = useRef(false);
  const hasCleanedOidcStorage = useRef(false);

  // UI状態のみstateで管理
  const [showLoading, setShowLoading] = useState(true);

  /**
   * 認証状態をチェック（TanStack Query統合版）
   */
  const checkAuthState = useCallback(async () => {
    if (isProcessing.current || hasCheckedAuth.current) {
      return;
    }

    // コールバックURL上では認証フロー完了として元の画面にリダイレクト
    const isCallbackUrl = window.location.pathname.includes(PAGE_PATHS.CALLBACK);
    if (isCallbackUrl) {
      setIsAuthReady(true);
      setShowLoading(false);
      hasCheckedAuth.current = true;
      setTimeout(() => {
        redirectToOriginalUrl();
      }, 500);
      return;
    }

    isProcessing.current = true;

    try {
      // HumHubトークンの有効性をAPIリクエストで検証
      try {
        const currentUser = await authApi.getCurrentUser();
        if (currentUser) {
          // TanStack Query のキャッシュに保存（グローバル状態の代替）
          updateGlobalUserInfo(queryClient, currentUser);

          // OIDC認証が完了した場合はストレージをクリーンアップ
          if (!hasCleanedOidcStorage.current) {
            cleanupOidcStorage();
            hasCleanedOidcStorage.current = true;
          }

          setIsAuthReady(true);
          setShowLoading(false);
          hasCheckedAuth.current = true;
          return;
        }
      } catch (error) {
        console.log("⚠️ HumHub token validation failed:", error);
        // トークンが無効な場合はユーザー情報をクリア
        updateGlobalUserInfo(queryClient, null);
      }

      // 認証フロー実行
      const result = await authFlow.executeAuthFlow();

      if (result.success) {
        // 認証成功後にユーザー情報を再取得
        try {
          const currentUser = await authApi.getCurrentUser();
          if (currentUser) {
            updateGlobalUserInfo(queryClient, currentUser);
          }
        } catch (error) {
          console.error("❌ Failed to get user info after auth success:", error);
        }

        // 認証成功時もストレージをクリーンアップ
        if (!hasCleanedOidcStorage.current) {
          cleanupOidcStorage();
          hasCleanedOidcStorage.current = true;
        }

        setIsAuthReady(true);
        setShowLoading(false);
        hasCheckedAuth.current = true;
      } else if (result.error?.includes("Redirecting")) {
        console.log("🔄 Authentication redirecting...");
      } else {
        console.log("❌ Authentication failed:", result.error);
        updateGlobalUserInfo(queryClient, null);
        setShowLoading(false);
      }
    } catch (error) {
      console.error("❌ Auth state check failed:", error);
      updateGlobalUserInfo(queryClient, null);
      setShowLoading(false);
    } finally {
      isProcessing.current = false;
    }
  }, [authFlow.executeAuthFlow, queryClient, setIsAuthReady, redirectToOriginalUrl]);

  // 初期化処理（一度だけ実行）
  useEffect(() => {
    if (!isInitialized.current) {
      setAuthFlowHandler(authFlow);
      isInitialized.current = true;
      checkAuthState();
    }
  }, [authFlow, checkAuthState]);

  // OIDC認証状態の変化を監視
  useEffect(() => {
    const handleAuthChange = async () => {
      if (isInitialized.current && !hasCheckedAuth.current && !isProcessing.current) {
        if (auth.isAuthenticated && auth.user?.access_token) {
          if (!hasCleanedOidcStorage.current) {
            cleanupOidcStorage();
            hasCleanedOidcStorage.current = true;
          }

          setTimeout(() => {
            checkAuthState();
          }, 500);
        }
      }
    };
    handleAuthChange();
  }, [auth.isAuthenticated, auth.user?.access_token, checkAuthState]);

  // ローディング画面
  if (showLoading || authFlow.isAuthenticating) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-blue-600 border-b-2" />
          <p className="text-gray-600">{t("common.authorizing")}</p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

/**
 * 認証プロバイダーコンポーネント
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const { t } = useTranslation();
  const [isAuthReady, setIsAuthReady] = useState(false);
  const [retryFunction, _setRetryFunction] = useState<() => void>(() => () => {});
  const oidcConfig = getOidcConfig();

  const contextValue = {
    isAuthReady,
    retry: retryFunction,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      <OidcAuthProvider {...oidcConfig}>
        <AuthStateManager setIsAuthReady={setIsAuthReady}>
          {isAuthReady ? (
            children
          ) : (
            <div className="flex min-h-screen items-center justify-center">
              <div className="text-center">
                <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-blue-600 border-b-2" />
                <p className="text-gray-600">{t("common.authorizing")}</p>
                <Button onClick={retryFunction} variant="outline">
                  {t("common.retry")}
                </Button>
              </div>
            </div>
          )}
        </AuthStateManager>
      </OidcAuthProvider>
    </AuthContext.Provider>
  );
};

export default AuthProvider;
